import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../../../../services/AxiosInstance";
import { notifySuccess, notifyError } from "jsx/components/CitoPlus/components/CitoPlusToastr";
import { Card, Button, Form, InputGroup, Badge, Modal, ButtonGroup } from "react-bootstrap";
import {
  CitoPlusLinkIconPrimary,
  CitoPlusLinkIconDanger,
} from "jsx/components/CitoPlus/components/CitoPlusLink";
import swal from "sweetalert";

const CATEGORIAS_URL = "/api/v3/zonas-sociales/activos/categorias/";

const CategoriasActivos = () => {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const fetchCategorias = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(CATEGORIAS_URL);
      const results = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];
      setCategorias(results);
    } catch (error) {
      notifyError("No fue posible obtener las categorías de activos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategorias();
  }, [fetchCategorias]);

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!nombre.trim()) {
      notifyError("El nombre de la categoría es obligatorio.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nombre: nombre.trim(),
        descripcion: descripcion.trim(),
      };
      const response = await axiosInstance.post(CATEGORIAS_URL, payload);
      notifySuccess("Categoría creada correctamente.");
      setNombre("");
      setDescripcion("");
      setCategorias((prev) => [response.data, ...prev]);
    } catch (error) {
      notifyError(
        "No fue posible crear la categoría. Revisa que no exista una con el mismo nombre."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (categoria) => {
    setEditingCategoria(categoria);
    setShowEditModal(true);
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    if (!editingCategoria?.nombre?.trim()) {
      notifyError("El nombre de la categoría es obligatorio.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nombre: editingCategoria.nombre.trim(),
        descripcion: editingCategoria.descripcion?.trim() || "",
      };
      await axiosInstance.patch(
        `${CATEGORIAS_URL}${editingCategoria.uuid}/`,
        payload
      );
      notifySuccess("Categoría actualizada correctamente.");
      setShowEditModal(false);
      setEditingCategoria(null);
      fetchCategorias();
    } catch (error) {
      notifyError(
        "No fue posible actualizar la categoría. Revisa que no exista una con el mismo nombre."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (categoria) => {
    swal({
      title: "¿Estás seguro?",
      text: `La categoría "${categoria.nombre}" será eliminada permanentemente.`,
      icon: "warning",
      buttons: ["Cancelar", "Eliminar"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await axiosInstance.delete(`${CATEGORIAS_URL}${categoria.uuid}/`);
          notifySuccess("Categoría eliminada correctamente.");
          fetchCategorias();
        } catch (error) {
          notifyError("No fue posible eliminar la categoría.");
        }
      }
    });
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getRandomColor = (id) => {
    const colors = [
      "bg-primary",
      "bg-success",
      "bg-info",
      "bg-warning",
      "bg-danger",
      "bg-secondary",
    ];
    return colors[id % colors.length];
  };

  return (
    <section className="card">
      <header className="card-header">
        <div className="d-flex flex-wrap align-items-center gap-2 w-100">
          <div className="d-flex flex-column me-auto flex-grow-1">
            <h2 className="card-title mb-0">
              <i className="fas fa-tags me-2 text-primary" />
              Categorías de Activos
            </h2>
            <div className="text-muted small">
              Administra las categorías para clasificar los activos del conjunto
            </div>
          </div>
          <Badge bg="secondary" className="fs-6">
            <i className="fas fa-list me-1" />
            {categorias.length}{" "}
            {categorias.length === 1 ? "categoría" : "categorías"}
          </Badge>
        </div>
      </header>

      <div className="card-body">
        {/* Formulario de creación */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body className="p-4">
            <h6 className="text-muted text-uppercase fs-12 mb-3">
              <i className="fas fa-plus-circle me-1" />
              Nueva Categoría
            </h6>
            <Form onSubmit={handleCreate}>
              <div className="row g-3 align-items-end">
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label>
                      Nombre <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <i className="fas fa-tag" />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Ej: Mobiliario"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        disabled={saving}
                      />
                    </InputGroup>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Descripción</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <i className="fas fa-align-left" />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Descripción opcional de la categoría"
                        value={descripcion}
                        onChange={(e) => setDescripcion(e.target.value)}
                        disabled={saving}
                      />
                    </InputGroup>
                  </Form.Group>
                </div>
                <div className="col-md-2">
                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100 text-white"
                    disabled={saving || !nombre.trim()}
                  >
                    {saving ? (
                      <span className="d-inline-flex align-items-center gap-2">
                        <i className="fas fa-circle-notch fa-spin" />
                        <span>Guardando...</span>
                      </span>
                    ) : (
                      <>
                        <i className="fas fa-save me-2" />
                        Guardar
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Form>
          </Card.Body>
        </Card>

        {/* Lista de categorías */}
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-white border-bottom-0 pt-4 pb-0">
            <h6 className="text-muted text-uppercase fs-12">
              <i className="fas fa-list-ul me-1" />
              Lista de Categorías
            </h6>
          </Card.Header>
          <Card.Body className="p-0">
            {loading && categorias.length === 0 ? (
              <div className="py-5 text-center">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <p className="text-muted mb-0">Cargando categorías...</p>
              </div>
            ) : categorias.length === 0 ? (
              <div className="py-5 text-center">
                <div className="mb-3">
                  <i className="fas fa-inbox fa-3x text-secondary opacity-25" />
                </div>
                <h6 className="text-muted">No hay categorías</h6>
                <p className="text-muted small mb-0">
                  Crea la primera categoría usando el formulario de arriba
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="border-0 ps-4" style={{ width: "80px" }}>
                        Icono
                      </th>
                      <th className="border-0">Nombre</th>
                      <th className="border-0">Descripción</th>
                      <th className="border-0 text-end pe-4">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categorias.map((cat, index) => (
                      <tr key={cat.uuid || cat.id}>
                        <td className="ps-4">
                          <div
                            className={`rounded-circle d-flex align-items-center justify-content-center text-white fw-bold ${getRandomColor(
                              cat.id
                            )}`}
                            style={{ width: "40px", height: "40px", fontSize: "14px" }}
                          >
                            {getInitials(cat.nombre)}
                          </div>
                        </td>
                        <td className="align-middle">
                          <span className="fw-medium">{cat.nombre}</span>
                        </td>
                        <td className="align-middle">
                          {cat.descripcion ? (
                            <span className="text-muted">{cat.descripcion}</span>
                          ) : (
                            <span className="text-muted fst-italic">
                              Sin descripción
                            </span>
                          )}
                        </td>
                        <td className="align-middle text-end pe-4">
                          <ButtonGroup aria-label="Acciones de la categoría">
                            <CitoPlusLinkIconPrimary
                              title="Editar categoría"
                              onClick={() => handleEdit(cat)}
                            >
                              <i className="fas fa-edit" />
                            </CitoPlusLinkIconPrimary>
                            <CitoPlusLinkIconDanger
                              title="Eliminar categoría"
                              onClick={() => handleDelete(cat)}
                            >
                              <i className="fas fa-trash-alt" />
                            </CitoPlusLinkIconDanger>
                          </ButtonGroup>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card.Body>
          {categorias.length > 0 && (
            <Card.Footer className="bg-white border-top-0 text-center py-3">
              <small className="text-muted">
                Mostrando {categorias.length}{" "}
                {categorias.length === 1 ? "categoría" : "categorías"}
              </small>
            </Card.Footer>
          )}
        </Card>
      </div>

      {/* Modal de edición */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-edit me-2 text-primary" />
            Editar Categoría
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdate}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>
                Nombre <span className="text-danger">*</span>
              </Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <i className="fas fa-tag" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  value={editingCategoria?.nombre || ""}
                  onChange={(e) =>
                    setEditingCategoria((prev) => ({
                      ...prev,
                      nombre: e.target.value,
                    }))
                  }
                  disabled={saving}
                />
              </InputGroup>
            </Form.Group>
            <Form.Group>
              <Form.Label>Descripción</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <i className="fas fa-align-left" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  value={editingCategoria?.descripcion || ""}
                  onChange={(e) =>
                    setEditingCategoria((prev) => ({
                      ...prev,
                      descripcion: e.target.value,
                    }))
                  }
                  disabled={saving}
                />
              </InputGroup>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowEditModal(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <i className="fas fa-circle-notch fa-spin me-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2" />
                  Guardar Cambios
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </section>
  );
};

export default CategoriasActivos;
